import React from 'react';

export type SkeletonVariant = 'text' | 'circle' | 'rectangle' | 'card';

export interface SkeletonProps {
  /** The variant of the skeleton */
  variant?: SkeletonVariant;
  /** Width of the skeleton (can be px, %, rem, etc.) */
  width?: string | number;
  /** Height of the skeleton (can be px, %, rem, etc.) */
  height?: string | number;
  /** Size for circle variant (will be used for both width and height) */
  size?: number;
  /** Additional className for custom styling */
  className?: string;
  /** Number of text lines to render (only for text variant) */
  lines?: number;
  /** Whether to animate the skeleton */
  animate?: boolean;
}

export interface SkeletonTableProps {
  /** Number of rows to display */
  rows?: number;
  /** Number of columns to display */
  columns?: number;
  /** Whether to show a header row */
  showHeader?: boolean;
  /** Additional className for custom styling */
  className?: string;
}

export interface SkeletonCardProps {
  /** Whether to show an image placeholder */
  showImage?: boolean;
  /** Number of text lines in the content */
  lines?: number;
  /** Whether to show action buttons */
  showActions?: boolean;
  /** Additional className for custom styling */
  className?: string;
}

const formatSize = (value: string | number | undefined): string | undefined => {
  if (value === undefined) return undefined;
  if (typeof value === 'number') return `${value}px`;
  return value;
};

const variantStyles: Record<SkeletonVariant, string> = {
  text: 'rounded',
  circle: 'rounded-full',
  rectangle: 'rounded-lg',
  card: 'rounded-lg',
};

const defaultDimensions: Record<SkeletonVariant, { width?: string; height?: string }> = {
  text: { width: '100%', height: '1rem' },
  circle: { width: '40px', height: '40px' },
  rectangle: { width: '100%', height: '100px' },
  card: { width: '100%', height: '200px' },
};

/**
 * Base Skeleton component for loading states
 */
export const Skeleton: React.FC<SkeletonProps> = ({
  variant = 'text',
  width,
  height,
  size,
  className = '',
  lines = 1,
  animate = true,
}) => {
  const baseClasses = `
    bg-gray-200
    ${animate ? 'animate-pulse' : ''}
    ${variantStyles[variant]}
  `.trim().replace(/\s+/g, ' ');

  // For circle variant, use size prop
  const computedWidth = variant === 'circle' && size
    ? formatSize(size)
    : formatSize(width) || defaultDimensions[variant].width;

  const computedHeight = variant === 'circle' && size
    ? formatSize(size)
    : formatSize(height) || defaultDimensions[variant].height;

  // Render multiple lines for text variant
  if (variant === 'text' && lines > 1) {
    return (
      <div className={`space-y-2 ${className}`}>
        {Array.from({ length: lines }).map((_, index) => (
          <div
            key={index}
            className={baseClasses}
            style={{
              width: index === lines - 1 ? '75%' : computedWidth,
              height: computedHeight,
            }}
            role="presentation"
            aria-hidden="true"
          />
        ))}
      </div>
    );
  }

  return (
    <div
      className={`${baseClasses} ${className}`.trim()}
      style={{
        width: computedWidth,
        height: computedHeight,
      }}
      role="presentation"
      aria-hidden="true"
    />
  );
};

Skeleton.displayName = 'Skeleton';

/**
 * SkeletonTable component for table loading states
 */
export const SkeletonTable: React.FC<SkeletonTableProps> = ({
  rows = 5,
  columns = 4,
  showHeader = true,
  className = '',
}) => {
  return (
    <div className={`overflow-x-auto ${className}`} role="presentation" aria-hidden="true">
      <table className="min-w-full divide-y divide-gray-200">
        {showHeader && (
          <thead className="bg-gray-50">
            <tr>
              {Array.from({ length: columns }).map((_, colIndex) => (
                <th
                  key={`header-${colIndex}`}
                  className="px-4 py-3"
                >
                  <Skeleton
                    variant="text"
                    width={colIndex === 0 ? '70%' : '50%'}
                    height="12px"
                    className="mx-auto"
                  />
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody className="bg-white divide-y divide-gray-200">
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <tr key={`row-${rowIndex}`}>
              {Array.from({ length: columns }).map((_, colIndex) => (
                <td
                  key={`cell-${rowIndex}-${colIndex}`}
                  className="px-4 py-3 whitespace-nowrap"
                >
                  <Skeleton
                    variant="text"
                    width={colIndex === 0 ? '80%' : `${60 + (colIndex % 3) * 15}%`}
                    height="14px"
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

SkeletonTable.displayName = 'SkeletonTable';

/**
 * SkeletonCard component for card loading states
 */
export const SkeletonCard: React.FC<SkeletonCardProps> = ({
  showImage = true,
  lines = 3,
  showActions = true,
  className = '',
}) => {
  return (
    <div
      className={`
        bg-white border border-gray-200 rounded-lg overflow-hidden
        ${className}
      `.trim().replace(/\s+/g, ' ')}
      role="presentation"
      aria-hidden="true"
    >
      {/* Image placeholder */}
      {showImage && (
        <Skeleton
          variant="rectangle"
          width="100%"
          height="160px"
          className="rounded-none"
        />
      )}

      {/* Content */}
      <div className="p-4">
        {/* Title */}
        <Skeleton
          variant="text"
          width="75%"
          height="20px"
          className="mb-3"
        />

        {/* Text lines */}
        <div className="space-y-2">
          {Array.from({ length: lines }).map((_, index) => (
            <Skeleton
              key={index}
              variant="text"
              width={index === lines - 1 ? '60%' : '100%'}
              height="14px"
            />
          ))}
        </div>

        {/* Actions */}
        {showActions && (
          <div className="mt-4 flex gap-2">
            <Skeleton variant="rectangle" width="80px" height="32px" />
            <Skeleton variant="rectangle" width="80px" height="32px" />
          </div>
        )}
      </div>
    </div>
  );
};

SkeletonCard.displayName = 'SkeletonCard';

/**
 * SkeletonList component for list loading states
 */
export interface SkeletonListProps {
  /** Number of items to display */
  items?: number;
  /** Whether to show an avatar */
  showAvatar?: boolean;
  /** Number of text lines per item */
  lines?: number;
  /** Additional className for custom styling */
  className?: string;
}

export const SkeletonList: React.FC<SkeletonListProps> = ({
  items = 5,
  showAvatar = true,
  lines = 2,
  className = '',
}) => {
  return (
    <div
      className={`divide-y divide-gray-200 ${className}`}
      role="presentation"
      aria-hidden="true"
    >
      {Array.from({ length: items }).map((_, index) => (
        <div key={index} className="flex items-start gap-4 py-4">
          {showAvatar && (
            <Skeleton variant="circle" size={40} />
          )}
          <div className="flex-1 min-w-0">
            <Skeleton
              variant="text"
              width="40%"
              height="16px"
              className="mb-2"
            />
            {Array.from({ length: lines - 1 }).map((_, lineIndex) => (
              <Skeleton
                key={lineIndex}
                variant="text"
                width={lineIndex === lines - 2 ? '70%' : '100%'}
                height="14px"
                className={lineIndex > 0 ? 'mt-1' : ''}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

SkeletonList.displayName = 'SkeletonList';

/**
 * SkeletonForm component for form loading states
 */
export interface SkeletonFormProps {
  /** Number of fields to display */
  fields?: number;
  /** Whether to show labels */
  showLabels?: boolean;
  /** Whether to show a submit button */
  showButton?: boolean;
  /** Additional className for custom styling */
  className?: string;
}

export const SkeletonForm: React.FC<SkeletonFormProps> = ({
  fields = 4,
  showLabels = true,
  showButton = true,
  className = '',
}) => {
  return (
    <div
      className={`space-y-4 ${className}`}
      role="presentation"
      aria-hidden="true"
    >
      {Array.from({ length: fields }).map((_, index) => (
        <div key={index}>
          {showLabels && (
            <Skeleton
              variant="text"
              width="25%"
              height="14px"
              className="mb-2"
            />
          )}
          <Skeleton
            variant="rectangle"
            width="100%"
            height="40px"
          />
        </div>
      ))}
      {showButton && (
        <div className="pt-2">
          <Skeleton
            variant="rectangle"
            width="120px"
            height="40px"
          />
        </div>
      )}
    </div>
  );
};

SkeletonForm.displayName = 'SkeletonForm';

/**
 * SkeletonDetail component for detail page loading states
 * Mimics the layout of a detail page with header, metadata, and content sections
 */
export interface SkeletonDetailProps {
  /** Whether to show the sidebar section */
  showSidebar?: boolean;
  /** Number of content sections */
  sections?: number;
  /** Additional className for custom styling */
  className?: string;
}

export const SkeletonDetail: React.FC<SkeletonDetailProps> = ({
  showSidebar = true,
  sections = 3,
  className = '',
}) => {
  return (
    <div className={`space-y-6 ${className}`} role="presentation" aria-hidden="true">
      {/* Back button skeleton */}
      <Skeleton variant="text" width="140px" height="20px" />

      {/* Header card */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4 flex-1">
            <Skeleton variant="circle" size={48} />
            <div className="flex-1">
              <Skeleton variant="text" width="60%" height="28px" className="mb-2" />
              <div className="flex gap-3">
                <Skeleton variant="text" width="100px" height="16px" />
                <Skeleton variant="text" width="80px" height="24px" />
              </div>
            </div>
          </div>
          <Skeleton variant="rectangle" width="100px" height="36px" />
        </div>

        {/* Metadata row */}
        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700 grid grid-cols-1 md:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton variant="rectangle" width="40px" height="40px" />
              <div>
                <Skeleton variant="text" width="80px" height="14px" className="mb-1" />
                <Skeleton variant="text" width="60px" height="18px" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Content area */}
      <div className={showSidebar ? 'grid grid-cols-1 lg:grid-cols-3 gap-6' : ''}>
        <div className={showSidebar ? 'lg:col-span-2 space-y-6' : 'space-y-6'}>
          {Array.from({ length: sections }).map((_, i) => (
            <div key={i} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <Skeleton variant="text" width="30%" height="20px" className="mb-4" />
              <Skeleton variant="text" lines={3} />
            </div>
          ))}
        </div>

        {showSidebar && (
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <Skeleton variant="text" width="50%" height="18px" className="mb-4" />
              <div className="space-y-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <Skeleton variant="circle" size={24} />
                    <Skeleton variant="text" width="80%" height="14px" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

SkeletonDetail.displayName = 'SkeletonDetail';

/**
 * SkeletonCardGrid component for card-based list pages (like People)
 */
export interface SkeletonCardGridProps {
  /** Number of cards to display */
  cards?: number;
  /** Number of columns */
  columns?: 2 | 3;
  /** Additional className for custom styling */
  className?: string;
}

export const SkeletonCardGrid: React.FC<SkeletonCardGridProps> = ({
  cards = 6,
  columns = 3,
  className = '',
}) => {
  const gridCols = columns === 2
    ? 'grid-cols-1 lg:grid-cols-2'
    : 'grid-cols-1 lg:grid-cols-2 xl:grid-cols-3';

  return (
    <div
      className={`grid gap-3 ${gridCols} ${className}`}
      role="presentation"
      aria-hidden="true"
    >
      {Array.from({ length: cards }).map((_, index) => (
        <div
          key={index}
          className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 sm:p-4"
        >
          <div className="flex items-start justify-between gap-2 sm:gap-3">
            <div className="flex items-start gap-2 sm:gap-3 min-w-0 flex-1">
              <Skeleton variant="circle" size={40} />
              <div className="flex-1 min-w-0">
                <Skeleton variant="text" width="70%" height="18px" className="mb-2" />
                <Skeleton variant="text" width="90%" height="14px" />
              </div>
            </div>
            <Skeleton variant="rectangle" width="60px" height="24px" />
          </div>
          <div className="mt-3 flex items-center justify-between">
            <Skeleton variant="text" width="80px" height="14px" />
            <Skeleton variant="text" width="60px" height="14px" />
          </div>
        </div>
      ))}
    </div>
  );
};

SkeletonCardGrid.displayName = 'SkeletonCardGrid';
